import React, { useState, useMemo } from 'react';
import * as utils from '../../common/Utils.js';

// Single pass over all stats to build per-player career and prior-season totals,
// instead of re-filtering the full stats list for every cell.
function buildPlayerStatTotals(allGameStats, games, prevSeason) {
  const gameSeasonByID = new Map(games.map(g => [g.id, g.season]));
  const totals = new Map();
  for (const s of allGameStats) {
    if (s.status !== 'played') continue;
    let t = totals.get(s.playerID);
    if (!t) {
      t = { gp: 0, points: 0, prevGP: 0, prevPoints: 0, prevRebounds: 0 };
      totals.set(s.playerID, t);
    }
    t.gp += 1;
    t.points += s.points;
    if (gameSeasonByID.get(s.gameID) == prevSeason) {
      t.prevGP += 1;
      t.prevPoints += s.points;
      t.prevRebounds += s.rebounds;
    }
  }
  return totals;
}

function formatPerGame(total, gp, label) {
  if (!gp) return '-';
  return `${(total / gp).toFixed(1)} ${label}`;
}

const DraftablePlayersList = ({
  draft,
  allGameStats,
  games,
  season,
  onDraftPlayer,
  computedDraftPicks,
}) => {
  const [modalPlayer, setModalPlayer] = useState(null);
  const [selectedPickNumber, setSelectedPickNumber] = useState(null);

  const statTotals = useMemo(
    () => buildPlayerStatTotals(allGameStats, games, season - 1),
    [allGameStats, games, season]
  );

  if (!draft || !draft.undraftedPlayers) return null;

  const draftedSet = new Set((draft.draftedPlayers || []).filter(id => id && id !== ''));

  const sortedPlayers = [...draft.undraftedPlayers].sort((a, b) => {
    const lastA = (a.lastName || '').toLowerCase();
    const lastB = (b.lastName || '').toLowerCase();
    const firstA = (a.firstName || '').toLowerCase();
    const firstB = (b.firstName || '').toLowerCase();
    if (lastA !== lastB) return lastA < lastB ? -1 : 1;
    return firstA < firstB ? -1 : firstA > firstB ? 1 : 0;
  });

  const emptyPicks = (computedDraftPicks || [])
    .filter(p => !p.playerID || p.playerID === '')
    .slice(0, 5);

  const openModal = (player) => {
    setModalPlayer(player);
    setSelectedPickNumber(emptyPicks.length > 0 ? emptyPicks[0].number : null);
  };

  const closeModal = () => {
    setModalPlayer(null);
    setSelectedPickNumber(null);
  };

  const confirmDraft = () => {
    if (!modalPlayer || selectedPickNumber === null) return;
    const pickIndex = selectedPickNumber - 1;
    onDraftPlayer(modalPlayer.id, pickIndex);
    closeModal();
  };

  return (
    <div className="league-table-container schedule">
      <p className="league-title-standard-secondary text-align-center">Players</p>
      <p className="league-title-standard-small text-align-center">Drafted players are greyed out</p>
      <div style={{ overflowX: 'auto' }}>
        <table className="table scroll-on-overflow">
          <thead>
            <tr>
              {onDraftPlayer && <th>#</th>}
              <th>Name</th>
              <th>Height</th>
              <th>Age</th>
              <th>Career PPG</th>
              <th>{`S${season - 1} PPG`}</th>
              <th>{`S${season - 1} RPG`}</th>
              {onDraftPlayer && <th>Draft</th>}
            </tr>
          </thead>
          <tbody>
            {sortedPlayers.map((player, index) => {
              const t = statTotals.get(player.id);
              return (
              <tr key={player.id} className={draftedSet.has(player.id) ? 'draft-player-taken' : ''}>
                {onDraftPlayer && <td style={{ color: '#888', fontSize: 13 }}>{index + 1}</td>}
                <td>
                  <a className="unstyled-link" href={`/players/${player.id}`}>
                    {utils.displayPlayerName(player)}
                  </a>
                </td>
                <td>{player.height.feet}' {player.height.inches}"</td>
                <td>{utils.getAgeForBirthday(player.birthday, player.age)}</td>
                <td>{formatPerGame(t?.points, t?.gp, 'PPG')}</td>
                <td>{formatPerGame(t?.prevPoints, t?.prevGP, 'PPG')}</td>
                <td>{formatPerGame(t?.prevRebounds, t?.prevGP, 'RPG')}</td>
                {onDraftPlayer && (
                  <td>
                    <button
                      className="btn btn-dark btn-sm"
                      disabled={draftedSet.has(player.id) || emptyPicks.length === 0}
                      onClick={() => openModal(player)}
                    >
                      Draft
                    </button>
                  </td>
                )}
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {onDraftPlayer && modalPlayer && (
        <div
          className="modal-backdrop"
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1050,
          }}
          onClick={closeModal}
        >
          <div
            style={{
              background: '#fff', borderRadius: 8, padding: '24px 28px',
              minWidth: 340, maxWidth: 480, width: '90%',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h5 style={{ marginBottom: 16 }}>
              Draft {utils.displayPlayerName(modalPlayer)}
            </h5>
            <p style={{ marginBottom: 8, color: '#555' }}>Select pick slot:</p>
            {emptyPicks.length === 0 ? (
              <p>No open pick slots available.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
                {emptyPicks.map(pick => (
                  <label
                    key={pick.number}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', border: '1px solid #ccc', borderRadius: 6,
                      cursor: 'pointer',
                      background: selectedPickNumber === pick.number ? '#f0f0f0' : '#fff',
                    }}
                  >
                    <input
                      type="radio"
                      name="pickSlot"
                      value={pick.number}
                      checked={selectedPickNumber === pick.number}
                      onChange={() => setSelectedPickNumber(pick.number)}
                    />
                    <span>
                      <strong>Pick #{pick.number}</strong>
                      {pick.team ? ` — ${pick.team.name}` : ''}
                      {pick.time ? ` — ${utils.formatDraftTime(pick.time)}` : ''}
                    </span>
                  </label>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={closeModal}>Cancel</button>
              <button
                className="btn btn-dark"
                disabled={selectedPickNumber === null || emptyPicks.length === 0}
                onClick={confirmDraft}
              >
                Confirm Draft
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DraftablePlayersList;
